/**
 * Shared Document Extraction Utilities
 *
 * Extracts text content from various document formats:
 * - XLSX/XLS (Excel spreadsheets)
 * - CSV (Comma-separated values)
 * - PDF (Portable Document Format)
 * - TXT (Plain text)
 *
 * Used by:
 * - Personalization panel (CPT code extraction from uploaded files)
 * - Chat attachments (document Q&A)
 */

import * as pdfParse from 'pdf-parse';
import { createLogger } from '@ctrl-enter/tools/utils';
import * as ExcelJS from 'exceljs';

const logger = createLogger('lib:tools');

// Maximum file size for text extraction (10MB)
// Prevents memory issues with very large files
const MAX_EXTRACTION_SIZE = 10 * 1024 * 1024;

// Recommended file size for optimal AI processing
// Large CSVs can create massive token counts that exceed OpenAI limits
const RECOMMENDED_EXTRACTION_SIZE = 2 * 1024 * 1024; // 2MB

// Maximum estimated token count after extraction
// OpenAI's context window is ~128K tokens, leave room for chat history
export const MAX_ESTIMATED_TOKENS = 100000;

export type DocumentExtractionResult =
  | {
      success: true;
      text: string;
      metadata?: {
        pageCount?: number;
        sheetCount?: number;
        filename?: string;
        extractionTimeMs?: number;
        estimatedTokens?: number;
        fileSizeMB?: number;
        warning?: string; // Warning message if file is large but processable
      };
    }
  | {
      success: false;
      error: string;
      details?: string;
      errorCode?: 'file_too_large' | 'content_too_large' | 'extraction_failed' | 'no_content';
    };

/**
 * Estimate token count from text (rough approximation)
 * OpenAI tokenization: ~4 characters per token on average
 */
function estimateTokenCount(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Check if extracted content is within acceptable limits
 */
type ErrorCode = 'file_too_large' | 'content_too_large' | 'extraction_failed' | 'no_content';

function validateExtractedContent(
  text: string,
  fileSizeMB: number
): {
  isValid: boolean;
  warning?: string;
  errorCode?: ErrorCode;
} {
  const estimatedTokens = estimateTokenCount(text);

  // Hard limit: Content too large for any AI model
  if (estimatedTokens > MAX_ESTIMATED_TOKENS) {
    return {
      isValid: false,
      errorCode: 'content_too_large',
    };
  }

  // Warning: File is large, might cause issues
  if (fileSizeMB > RECOMMENDED_EXTRACTION_SIZE / (1024 * 1024)) {
    return {
      isValid: true,
      warning: `Large file (${fileSizeMB.toFixed(1)}MB) - consider splitting into smaller files for better AI processing`,
    };
  }

  return { isValid: true };
}

/**
 * Extract text from Excel files (.xlsx, .xls)
 * Converts all sheets to CSV format and combines them
 */
export async function extractTextFromExcel(
  buffer: Buffer | ArrayBuffer,
  filename?: string
): Promise<DocumentExtractionResult> {
  try {
    const startTime = Date.now();
    const bufferSize = buffer.byteLength || (buffer as Buffer).length;
    const fileSizeMB = bufferSize / (1024 * 1024);

    logger.info('📊 [EXCEL-EXTRACT] Starting Excel extraction:', {
      filename,
      bufferSize,
      fileSizeMB: fileSizeMB.toFixed(2),
    });

    // Pre-flight check: File too large
    if (bufferSize > MAX_EXTRACTION_SIZE) {
      logger.error('❌ [EXCEL-EXTRACT] File too large:', { bufferSize, max: MAX_EXTRACTION_SIZE });
      return {
        success: false,
        error: `File size (${fileSizeMB.toFixed(1)}MB) exceeds the maximum limit for AI processing (${MAX_EXTRACTION_SIZE / (1024 * 1024)}MB)`,
        details: 'Please split your spreadsheet into smaller files or reduce the data volume',
        errorCode: 'file_too_large',
      };
    }

    const workbook = new ExcelJS.Workbook();
    const nodeBuffer: Buffer =
      buffer instanceof Buffer
        ? buffer
        : (() => {
            const u8 = new Uint8Array(buffer as ArrayBuffer);
            const b = Buffer.allocUnsafe(u8.byteLength);
            b.set(u8);
            return b;
          })();
    // @types/node 24.x made Buffer generic (Buffer<ArrayBufferLike>) but exceljs
    // still uses the pre-generic Buffer definition — runtime identical, cast is safe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await workbook.xlsx.load(nodeBuffer as unknown as any);

    if (workbook.worksheets.length === 0) {
      return {
        success: false,
        error: 'Excel file contains no sheets',
        errorCode: 'no_content',
      };
    }

    // Extract text from all sheets — convert each to CSV-style text
    const sheetTexts: string[] = [];
    workbook.eachSheet((worksheet: ExcelJS.Worksheet) => {
      const rows: string[] = [];
      worksheet.eachRow((row: ExcelJS.Row) => {
        const values = (row.values as ExcelJS.CellValue[]).slice(1); // row.values is 1-indexed
        const cells = values.map(cell => {
          if (cell === null || cell === undefined) return '';
          if (typeof cell === 'object' && 'result' in cell) return String((cell as ExcelJS.CellFormulaValue).result ?? '');
          if (typeof cell === 'object' && 'hyperlink' in cell) return String((cell as ExcelJS.CellHyperlinkValue).text ?? '');
          if (typeof cell === 'object' && 'richText' in cell) return (cell as ExcelJS.CellRichTextValue).richText.map((r: ExcelJS.RichText) => r.text).join('');
          if (typeof cell === 'object' && cell instanceof Date) return cell.toISOString().split('T')[0] ?? '';
          return String(cell);
        });
        rows.push(cells.join(','));
      });
      const csvText = rows.join('\n');
      if (csvText.trim()) {
        sheetTexts.push(`=== Sheet: ${worksheet.name} ===\n${csvText}`);
      }
    });

    if (sheetTexts.length === 0) {
      return {
        success: false,
        error: 'No readable content found in Excel file',
        errorCode: 'no_content',
      };
    }

    const combinedText = sheetTexts.join('\n\n');
    const extractionTimeMs = Date.now() - startTime;

    // Post-extraction validation
    const validation = validateExtractedContent(combinedText, fileSizeMB);

    if (!validation.isValid) {
      logger.error('❌ [EXCEL-EXTRACT] Content too large for AI processing:', {
        filename,
        textLength: combinedText.length,
        estimatedTokens: estimateTokenCount(combinedText),
      });

      return {
        success: false,
        error: `Spreadsheet content is too large for AI processing (${estimateTokenCount(combinedText).toLocaleString()} tokens estimated)`,
        details: `OpenAI's limit is ~${MAX_ESTIMATED_TOKENS.toLocaleString()} tokens. Please reduce data volume or split into smaller files.`,
        ...(validation.errorCode !== undefined && { errorCode: validation.errorCode }),
      };
    }

    logger.info('✅ [EXCEL-EXTRACT] Extraction successful:', {
      filename,
      sheetCount: workbook.worksheets.length,
      textLength: combinedText.length,
      estimatedTokens: estimateTokenCount(combinedText),
      extractionTimeMs,
    });

    return {
      success: true,
      text: combinedText,
      metadata: {
        sheetCount: workbook.worksheets.length,
        estimatedTokens: estimateTokenCount(combinedText),
        fileSizeMB,
        extractionTimeMs,
        ...(filename ? { filename } : {}),
        ...(validation.warning ? { warning: validation.warning } : {}),
      },
    };
  } catch (error) {
    logger.error('❌ [EXCEL-EXTRACT] Extraction failed:', error);
    return {
      success: false,
      error: 'Failed to extract text from Excel file',
      details: error instanceof Error ? error.message : String(error),
      errorCode: 'extraction_failed',
    };
  }
}

/**
 * Extract text from CSV files
 * Simply reads and returns the text content
 */
export async function extractTextFromCSV(
  buffer: Buffer | ArrayBuffer,
  filename?: string
): Promise<DocumentExtractionResult> {
  try {
    const startTime = Date.now();
    const bufferSize = buffer.byteLength || (buffer as Buffer).length;
    const fileSizeMB = bufferSize / (1024 * 1024);

    logger.info('📝 [CSV-EXTRACT] Starting CSV extraction:', {
      filename,
      bufferSize,
      fileSizeMB: fileSizeMB.toFixed(2),
    });

    // Pre-flight check: File too large
    if (bufferSize > MAX_EXTRACTION_SIZE) {
      logger.error('❌ [CSV-EXTRACT] File too large:', { bufferSize, max: MAX_EXTRACTION_SIZE });
      return {
        success: false,
        error: `File size (${fileSizeMB.toFixed(1)}MB) exceeds the maximum limit for AI processing (${MAX_EXTRACTION_SIZE / (1024 * 1024)}MB)`,
        details: 'Please split your CSV into smaller files or reduce the data volume',
        errorCode: 'file_too_large',
      };
    }

    // Convert to string
    const text =
      buffer instanceof Buffer ? buffer.toString('utf-8') : new TextDecoder('utf-8').decode(buffer);

    if (!text || text.trim().length === 0) {
      return {
        success: false,
        error: 'CSV file appears to be empty',
        errorCode: 'no_content',
      };
    }

    const extractionTimeMs = Date.now() - startTime;

    // Post-extraction validation
    const validation = validateExtractedContent(text, fileSizeMB);

    if (!validation.isValid) {
      logger.error('❌ [CSV-EXTRACT] Content too large for AI processing:', {
        filename,
        textLength: text.length,
        estimatedTokens: estimateTokenCount(text),
      });

      return {
        success: false,
        error: `CSV content is too large for AI processing (${estimateTokenCount(text).toLocaleString()} tokens estimated)`,
        details: `OpenAI's limit is ~${MAX_ESTIMATED_TOKENS.toLocaleString()} tokens. Please reduce data volume or split into smaller files.`,
        ...(validation.errorCode !== undefined && { errorCode: validation.errorCode }),
      };
    }

    logger.info('✅ [CSV-EXTRACT] Extraction successful:', {
      filename,
      textLength: text.length,
      estimatedTokens: estimateTokenCount(text),
      extractionTimeMs,
    });

    const result: DocumentExtractionResult = {
      success: true,
      text,
      metadata: {
        estimatedTokens: estimateTokenCount(text),
        fileSizeMB,
        extractionTimeMs,
        ...(filename ? { filename } : {}),
        ...(validation.warning ? { warning: validation.warning } : {}),
      },
    };

    return result;
  } catch (error) {
    logger.error('❌ [CSV-EXTRACT] Extraction failed:', error);
    return {
      success: false,
      error: 'Failed to extract text from CSV file',
      details: error instanceof Error ? error.message : String(error),
      errorCode: 'extraction_failed',
    };
  }
}

/**
 * Extract text from PDF files
 * Uses pdf-parse library to extract text content
 */
export async function extractTextFromPDF(
  buffer: Buffer | ArrayBuffer,
  filename?: string
): Promise<DocumentExtractionResult> {
  try {
    logger.info('📄 [PDF-EXTRACT] Starting PDF extraction:', {
      filename,
      bufferSize: buffer.byteLength || (buffer as Buffer).length,
    });

    // Convert ArrayBuffer to Buffer if needed
    const pdfBuffer = buffer instanceof Buffer ? buffer : Buffer.from(new Uint8Array(buffer));
    const pdfData = await (pdfParse as any)(pdfBuffer);

    if (!pdfData.text || pdfData.text.trim().length === 0) {
      return {
        success: false,
        error: 'No extractable text found in PDF (may contain only images)',
      };
    }

    logger.info('✅ [PDF-EXTRACT] Extraction successful:', {
      filename,
      pageCount: pdfData.numpages,
      textLength: pdfData.text.length,
    });

    return {
      success: true,
      text: pdfData.text,
      metadata: {
        pageCount: pdfData.numpages,
        ...(filename ? { filename } : {}),
      },
    };
  } catch (error) {
    logger.error('❌ [PDF-EXTRACT] Extraction failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to extract PDF content',
    };
  }
}

/**
 * Extract text from plain text files
 */
export async function extractTextFromPlainText(
  buffer: Buffer | ArrayBuffer,
  filename?: string
): Promise<DocumentExtractionResult> {
  try {
    logger.info('📝 [TXT-EXTRACT] Starting text extraction:', {
      filename,
      bufferSize: buffer.byteLength || (buffer as Buffer).length,
    });

    // Convert to string
    const text =
      buffer instanceof Buffer ? buffer.toString('utf-8') : new TextDecoder('utf-8').decode(buffer);

    if (!text || text.trim().length === 0) {
      return {
        success: false,
        error: 'Text file appears to be empty',
      };
    }

    logger.info('✅ [TXT-EXTRACT] Extraction successful:', {
      filename,
      textLength: text.length,
    });

    const result: DocumentExtractionResult = {
      success: true,
      text,
    };

    if (filename) {
      result.metadata = { filename };
    }

    return result;
  } catch (error) {
    logger.error('❌ [TXT-EXTRACT] Extraction failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to extract text content',
    };
  }
}

/**
 * Main extraction function - routes to appropriate extractor based on file extension or MIME type
 */
export async function extractDocumentText(
  buffer: Buffer | ArrayBuffer,
  filenameOrMimeType: string
): Promise<DocumentExtractionResult> {
  const startTime = performance.now();
  const bufferSize = buffer.byteLength || (buffer as Buffer).length;

  logger.info('🔍 [DOC-EXTRACT] Starting document extraction:', {
    filenameOrMimeType,
    bufferSize,
    maxSize: MAX_EXTRACTION_SIZE,
  });

  // Check file size limit
  if (bufferSize > MAX_EXTRACTION_SIZE) {
    const sizeMB = (bufferSize / (1024 * 1024)).toFixed(1);
    const maxSizeMB = (MAX_EXTRACTION_SIZE / (1024 * 1024)).toFixed(0);
    logger.error(`❌ [DOC-EXTRACT] File too large: ${sizeMB}MB (max: ${maxSizeMB}MB)`);
    return {
      success: false,
      error: `File too large for text extraction`,
      details: `File size is ${sizeMB}MB. Maximum size for extraction is ${maxSizeMB}MB.`,
    };
  }

  // Determine file type from extension or MIME type
  const lowerInput = filenameOrMimeType.toLowerCase();

  // Check extension first (from filename)
  let result: DocumentExtractionResult;

  if (lowerInput.endsWith('.xlsx') || lowerInput.includes('spreadsheetml.sheet')) {
    result = await extractTextFromExcel(buffer, filenameOrMimeType);
  } else if (lowerInput.endsWith('.xls') || lowerInput.includes('vnd.ms-excel')) {
    result = await extractTextFromExcel(buffer, filenameOrMimeType);
  } else if (lowerInput.endsWith('.csv') || lowerInput.includes('text/csv')) {
    result = await extractTextFromCSV(buffer, filenameOrMimeType);
  } else if (lowerInput.endsWith('.pdf') || lowerInput.includes('application/pdf')) {
    result = await extractTextFromPDF(buffer, filenameOrMimeType);
  } else if (
    lowerInput.endsWith('.txt') ||
    lowerInput.includes('text/plain') ||
    lowerInput.includes('text/markdown')
  ) {
    result = await extractTextFromPlainText(buffer, filenameOrMimeType);
  } else {
    result = {
      success: false,
      error: `Unsupported document type: ${filenameOrMimeType}`,
    };
  }

  // Add timing metrics
  const duration = performance.now() - startTime;

  if (result.success) {
    logger.info(
      `⏱️ [DOC-EXTRACT] Extraction completed in ${duration.toFixed(0)}ms, extracted ${result.text.length} characters`
    );

    // Add timing to metadata
    return {
      ...result,
      metadata: {
        ...result.metadata,
        extractionTimeMs: Math.round(duration),
      },
    };
  } else {
    logger.info(`⏱️ [DOC-EXTRACT] Extraction failed after ${duration.toFixed(0)}ms:`, result.error);
    return result;
  }
}

/**
 * Check if a filename or MIME type is a document that needs text extraction
 */
export function isExtractableDocument(filenameOrMimeType: string): boolean {
  const lower = filenameOrMimeType.toLowerCase();

  return (
    // Extensions
    lower.endsWith('.xlsx') ||
    lower.endsWith('.xls') ||
    lower.endsWith('.csv') ||
    lower.endsWith('.pdf') ||
    lower.endsWith('.txt') ||
    // MIME types
    lower.includes('spreadsheetml.sheet') ||
    lower.includes('vnd.ms-excel') ||
    lower.includes('text/csv') ||
    lower.includes('application/pdf') ||
    lower.includes('text/plain') ||
    lower.includes('text/markdown')
  );
}

/**
 * Format extracted text for AI consumption with metadata
 */
export function formatExtractedTextForAI(
  result: DocumentExtractionResult,
  originalFilename: string
): string {
  if (!result.success) {
    const errorDetails = result.details ? `\n${result.details}` : '';
    return `[Document: ${originalFilename}]\n\nError: ${result.error}${errorDetails}`;
  }

  const header = `[Document: ${originalFilename}]`;
  const metadata: string[] = [];

  if (result.metadata?.pageCount) {
    metadata.push(`Pages: ${result.metadata.pageCount}`);
  }
  if (result.metadata?.sheetCount) {
    metadata.push(`Sheets: ${result.metadata.sheetCount}`);
  }
  if (result.metadata?.extractionTimeMs) {
    metadata.push(`Extracted in ${result.metadata.extractionTimeMs}ms`);
  }

  const metadataLine = metadata.length > 0 ? `\n${metadata.join(' | ')}` : '';

  return `${header}${metadataLine}\n\n${result.text}`;
}
